/**
 * Naval predicate — the SINGLE source of truth for "is this entity a ship".
 *
 * Naval-ness is derived from the unit's class (EntityClass "naval") rather than a
 * separate flag/Set, so there's one fact to keep in sync. Pathfinding, orders,
 * combat and production all import THIS to decide the water-only movement domain.
 */
import type { World } from "./world";
import type { Eid, Vec2 } from "./types";

/** True when `eid` is a ship (class "naval") — moves on water, not land. */
export function isNaval(world: World, eid: Eid): boolean {
  return world.ident.get(eid)?.cls === "naval";
}

/** The movement domain an entity routes in. */
export type MoveDomain = "land" | "naval";

/** Domain for `eid`: naval ships sail on water, everything else walks on land. */
export function domainOf(world: World, eid: Eid): MoveDomain {
  return isNaval(world, eid) ? "naval" : "land";
}

/** The domain a RESOURCE node lives in: a fish school on water is "naval", a
 *  tree/rock/field/carcass on land is "land". */
export function nodeDomain(world: World, node: Eid): MoveDomain {
  const tr = world.transform.get(node);
  const h = tr ? world.hexAtWorld(tr.pos.x, tr.pos.y) : null;
  return h && (h.biome === "water" || h.biome === "deepWater") ? "naval" : "land";
}

/** Can `gatherer` actually REACH `node` to harvest it? Only when they share a
 *  movement domain — a land worker can never reach a fish school on the water
 *  (and a fishing boat can't reach a tree on land), so it must not be ordered to. */
export function canGatherNode(world: World, gatherer: Eid, node: Eid): boolean {
  return domainOf(world, gatherer) === nodeDomain(world, node);
}

/**
 * The land hex a boarder should walk to in order to embark a transport: the
 * passable LAND hex CLOSEST TO THE SHIP that lies on the boarding unit's SIDE of
 * the water (the half-plane facing the unit). This walks the unit right up to the
 * coast next to the ship — while the half-plane filter rejects the FAR shore that
 * nearestWalkableHex would wrongly pick when a unit and ship straddle a narrow
 * sea (the far shore is a closer ring to the ship). Returns the hex centre, or
 * null if no land sits near the ship (it's in deep open water → the unit waits).
 */
export function boardingShore(world: World, ship: Eid, ux: number, uy: number, radius = 8): Vec2 | null {
  const st = world.transform.get(ship);
  if (!st) return null;
  const sh = world.hexAtWorld(st.pos.x, st.pos.y);
  if (!sh) return null;
  // direction from the ship toward the unit — land hexes must point this way, so
  // a unit on the west shore never gets sent to the east shore (and vice-versa).
  const toUx = ux - st.pos.x, toUy = uy - st.pos.y;
  let best: [number, number] | null = null;
  let bestD = Infinity;
  for (let dc = -radius; dc <= radius; dc++) {
    for (let dr = -radius; dr <= radius; dr++) {
      const c = sh.col + dc, r = sh.row + dr;
      if (!world.passableFor("land", c, r)) continue;
      const ctr = world.hexCenter(c, r);
      if ((ctr.x - st.pos.x) * toUx + (ctr.y - st.pos.y) * toUy < 0) continue; // wrong side of the sea
      const d = (ctr.x - st.pos.x) ** 2 + (ctr.y - st.pos.y) ** 2; // nearest the SHIP
      // lower-(col,row) tie-break keeps the choice iteration-order-independent.
      if (d < bestD || (d === bestD && (c < best![0] || (c === best![0] && r < best![1])))) {
        bestD = d; best = [c, r];
      }
    }
  }
  return best ? world.hexCenter(best[0], best[1]) : null;
}

/** The open-water hex a transport should sail to in order to pick up units near
 *  (x,y): the WATER-EDGE hex (sea touching land) nearest that point, so the ship
 *  pulls right up to the boarders' coast. Returns its centre, or null if no coast
 *  is in reach. Deterministic (distance then col,row). */
export function nearestWaterEdge(world: World, x: number, y: number, radius = 18): Vec2 | null {
  const h0 = world.hexAtWorld(x, y);
  if (!h0) return null;
  let best: [number, number] | null = null;
  let bestD = Infinity;
  for (let dc = -radius; dc <= radius; dc++) {
    for (let dr = -radius; dr <= radius; dr++) {
      const c = h0.col + dc, r = h0.row + dr;
      if (!world.isWaterEdge(c, r)) continue;
      const ctr = world.hexCenter(c, r);
      const d = (ctr.x - x) ** 2 + (ctr.y - y) ** 2;
      if (d < bestD || (d === bestD && (c < best![0] || (c === best![0] && r < best![1])))) {
        bestD = d; best = [c, r];
      }
    }
  }
  return best ? world.hexCenter(best[0], best[1]) : null;
}
